import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaUserTie, FaUserGraduate, FaUserCog, FaMedal } from 'react-icons/fa'; // # Import icons from react-icons
import DashboardHeader from '../../../../components/DashboardHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { auth, db } from '../../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../../auth/cache_manager';
import './users.css';

// # Main Users component with navigation icons
const Users = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Navigation options with icons and paths
  const userOptions = [
    {
      title: 'UserBase',
      icon: <FaUsers className="users_icon" />,
      description: 'Manage all users and their accounts',
      path: '/admin/users/management'
    },
    {
      title: 'Employees',
      icon: <FaUserTie className="users_icon" />,
      description: 'Manage administrative users and tutors',
      path: '/admin/users/admins-tutors'
    },
    {
      title: 'Our Mentees',
      icon: <FaUserGraduate className="users_icon" />,
      description: 'View and manage student mentees',
      path: '/admin/users/mentees'
    },
    {
      title: 'User Controls',
      icon: <FaUserCog className="users_icon" />,
      description: 'Advanced user control settings',
      path: '/admin/users/controls'
    },
    {
      title: 'User Honors',
      icon: <FaMedal className="users_icon" />,
      description: 'Manage user achievements and honors',
      path: '/admin/users/honors'
    }
  ];

  // # Handle navigation to selected option
  const handleOptionClick = (path) => {
    navigate(path);
  };

  return (
    <div className={`users_container ${isDarkMode ? 'users_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="users_content">
        <h1 className="users_title">User Management Dashboard</h1>
        <p className="users_subtitle">Select a category to manage users</p>
        
        <div className="users_options_grid">
          {userOptions.map((option, index) => (
            <div 
              key={index} 
              className="users_option_card"
              onClick={() => handleOptionClick(option.path)}
            >
              <div className="users_option_icon">
                {option.icon}
              </div>
              <h2 className="users_option_title">{option.title}</h2>
              <p className="users_option_description">{option.description}</p>
            </div>
          ))}
        </div>
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default Users;
